import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import fishLogo from '@/assets/fish-logo-white.webp';
import { useContactInfo, telHref, mailHref } from '@/hooks/useContactInfo';

const linksCol1 = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Our Services' },
  { to: '/get-involved', label: 'Get Involved' },
  { to: '/about', label: 'About Us' },
  { to: '/login', label: 'Admin' },
];

const linksCol2 = [
  { to: '/news', label: 'Latest' },
  { to: '/contact', label: 'Contact' },
  { to: '/volunteers/resources', label: 'Driver Resources' },
  { to: '/accessibility', label: 'Accessibility' },
  { to: '/policies', label: 'Policies & Accounts' },
];

export default function Footer() {
  const contact = useContactInfo();
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

          {/* Branding */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <img src={fishLogo} alt="FISH Volunteer Centre logo" className="h-12 w-auto" />
            </Link>
            <p className="text-primary-foreground/80 text-base leading-relaxed">
              FISH Volunteer Centre is a charity registered in England and Wales (1189325).
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-cerulean">Contact Us</h3>
            <ul className="space-y-3 text-base">
              <li>
                <a href={telHref(contact.phone)} className="flex items-start gap-2 hover:text-cerulean transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{contact.phone}</span>
                </a>
              </li>
              <li>
                <a href={mailHref(contact.email)} className="flex items-start gap-2 hover:text-cerulean transition-colors break-all">
                  <Mail className="w-5 h-5 mt-0.5 shrink-0" />
                  <span>{contact.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" />
                <span>{contact.address}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-5 h-5 mt-0.5 shrink-0" />
                <span>{contact.hours}</span>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/share/19vqctZuMW/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 hover:text-cerulean transition-colors"
                  aria-label="FISH Volunteer Centre on Facebook"
                >
                  <svg className="w-5 h-5 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span>Facebook</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links - two columns */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-cerulean">Quick Links</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-base">
              <ul className="space-y-2">
                {linksCol1.map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="hover:text-cerulean transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
              <ul className="space-y-2">
                {linksCol2.map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="hover:text-cerulean transition-colors">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>

        <div className="mt-10 pt-6 border-t border-primary-foreground/20 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-primary-foreground/60">
          <span>© {new Date().getFullYear()} FISH Volunteer Centre. Registered Charity. All rights reserved.</span>
          <a href="https://studiozweig.com" target="_blank" rel="noopener noreferrer" className="hover:text-cerulean transition-colors">
            Website by Studio Zweig
          </a>
        </div>
      </div>
    </footer>
  );
}
